import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  BarChart,
  TrendingUp,
  People,
  Business,
  DirectionsCar,
  Assessment,
} from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import AnalyticsCharts from '../components/analytics/AnalyticsCharts';
import analyticsService, { 
  AnalyticsData, 
  formatPrice,
  processAnalyticsData,
  RawReport 
} from '../services/analyticsService';
import { format } from 'date-fns';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color }) => (
  <Card elevation={3} sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ pb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box 
          sx={{ 
            backgroundColor: color, 
            borderRadius: '50%', 
            p: 1.5, 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="h6" color="text.primary" fontWeight="500">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const AnalyticsPage: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        try {
          // Try to fetch from the actual API
          const data = await analyticsService.getAnalytics();
          setAnalyticsData(data);
        } catch (apiError) {
          console.warn('API not available, using mock data for demonstration:', apiError);
          
          // Fallback to mock data for demonstration
          const mockReports: RawReport[] = [
            {
              user_id: 'user1',
              dealership_name: 'Mercedes-Benz USA',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$65,000'
                  }
                }
              },
              vehicle_info: { make: 'Mercedes-Benz', model: 'C-Class' },
              title: '2023 Mercedes-Benz C-Class - Purchase Analysis Report',
              created_at: new Date('2025-08-01')
            },
            {
              user_id: 'user2',
              dealership_name: 'Ted Britt Ford Lincoln of Chantilly',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$45,000'
                  }
                }
              },
              vehicle_info: { make: 'Ford', model: 'Expedition' },
              title: '2022 Ford Expedition Max Limited - Purchase Analysis Report',
              created_at: new Date('2025-08-02')
            },
            {
              user_id: 'user1',
              dealership_name: 'BMW of Manhattan',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$78,000'
                  }
                }
              },
              vehicle_info: { make: 'BMW', model: 'X5' },
              title: '2023 BMW X5 - Purchase Analysis Report',
              created_at: new Date('2025-08-03')
            },
            {
              user_id: 'user3',
              dealership_name: 'Mercedes-Benz USA',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$85,000'
                  }
                }
              },
              vehicle_info: { make: 'Mercedes-Benz', model: 'E-Class' },
              title: '2024 Mercedes-Benz E-Class - Purchase Analysis Report',
              created_at: new Date('2025-08-04')
            },
            {
              user_id: 'user4',
              dealership_name: 'Audi Tysons Corner',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$72,500'
                  }
                }
              },
              vehicle_info: { make: 'Audi', model: 'Q7' },
              title: '2024 Audi Q7 Premium Plus - Purchase Analysis Report',
              created_at: new Date('2025-08-05')
            },
            {
              user_id: 'user2',
              dealership_name: 'Ted Britt Ford Lincoln of Chantilly',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$52,000'
                  }
                }
              },
              vehicle_info: { make: 'Lincoln', model: 'Navigator' },
              title: '2023 Lincoln Navigator - Purchase Analysis Report',
              created_at: new Date('2025-08-06')
            },
            {
              user_id: 'user5',
              dealership_name: 'Lexus of Rockville',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$48,900'
                  }
                }
              },
              vehicle_info: { make: 'Lexus', model: 'RX' },
              title: '2024 Lexus RX 350 - Purchase Analysis Report',
              created_at: new Date('2025-08-07')
            },
            {
              user_id: 'user6',
              dealership_name: 'BMW of Manhattan',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$95,000'
                  }
                }
              },
              vehicle_info: { make: 'BMW', model: 'X7' },
              title: '2024 BMW X7 xDrive40i - Purchase Analysis Report',
              created_at: new Date('2025-08-08')
            },
            // Test entries with default values (should be filtered out)
            {
              user_id: 'user7',
              dealership_name: 'Unknown',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$50,000'
                  }
                }
              },
              vehicle_info: { make: 'Car', model: 'Car Purchase' },
              title: 'Car Purchase - Purchase Analysis Report',
              created_at: new Date('2025-08-09')
            },
            {
              user_id: 'user8',
              dealership_name: '',
              report_content: {
                strategic_analysis: {
                  key_financial_data: {
                    current_price: '$25,000'
                  }
                }
              },
              vehicle_info: { make: 'Vehicle', model: 'Model' },
              title: 'Vehicle Model - Purchase Analysis Report',
              created_at: new Date('2025-08-10'),
              state: 'NY'
            }
          ];

          const processedData = processAnalyticsData(mockReports);
          setAnalyticsData(processedData);
        }
      } catch (err: any) {
        console.error('Error fetching analytics data:', err);
        setError(err.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Box textAlign="center">
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Loading Analytics Data...
              </Typography>
            </Box>
          </Box>
        </Container>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Container>
      </AppLayout>
    );
  }

  if (!analyticsData) {
    return (
      <AppLayout>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="info">
            No analytics data available.
          </Alert>
        </Container>
      </AppLayout>
    );
  }

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  return (
    <AppLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box mb={4}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            <BarChart sx={{ mr: 2, verticalAlign: 'middle' }} />
            Analytics Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Car Reports Analytics & Insights
          </Typography>
          
          {/* Date Range and Status */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip 
              label={`${formatDate(analyticsData.dateRange.start)} - ${formatDate(analyticsData.dateRange.end)}`}
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={`Last updated: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`}
              color="success" 
              variant="outlined" 
            />
          </Box>
        </Box>

        {/* Summary Statistics Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr 1fr'
            },
            gap: 3,
            mb: 4
          }}
        >
          <StatCard
            title="Total Reports"
            value={analyticsData.totalReports}
            subtitle="All time"
            icon={<Assessment />}
            color="#1976d2"
          />
          <StatCard
            title="Active Users"
            value={analyticsData.totalUsers}
            subtitle="Unique users"
            icon={<People />}
            color="#2e7d32"
          />
          <StatCard
            title="Dealerships"
            value={analyticsData.totalDealerships}
            subtitle="Analyzed"
            icon={<Business />}
            color="#ed6c02"
          />
          <StatCard
            title="Vehicle Makes"
            value={analyticsData.vehicleMakes.length}
            subtitle="Different brands"
            icon={<DirectionsCar />}
            color="#9c27b0"
          />
        </Box>

        {/* Top Performers Section */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr 1fr'
            },
            gap: 3,
            mb: 4
          }}
        >
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
              Top Dealerships by Value
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {analyticsData.dealershipPrices.slice(0, 5).map((item, index) => (
              <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    {item.dealership.length > 25 ? `${item.dealership.substring(0, 25)}...` : item.dealership}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.reportCount} reports
                  </Typography>
                </Box>
                <Chip 
                  label={formatPrice(item.totalPrice)} 
                  color="primary" 
                  size="small"
                />
              </Box>
            ))}
          </Paper>

          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
              Most Active Dealerships
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {analyticsData.dealershipReportCounts.slice(0, 5).map((item, index) => (
              <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    {item.dealership.length > 25 ? `${item.dealership.substring(0, 25)}...` : item.dealership}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.uniqueUsers} unique users
                  </Typography>
                </Box>
                <Chip 
                  label={`${item.reportCount} reports`} 
                  color="success" 
                  size="small"
                />
              </Box>
            ))}
          </Paper>

          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              <DirectionsCar sx={{ mr: 1, verticalAlign: 'middle' }} />
              Popular Vehicle Makes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {analyticsData.vehicleMakes.slice(0, 5).map((item, index) => (
              <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                <Typography variant="body1" fontWeight="500">
                  {item.make}
                </Typography>
                <Chip 
                  label={`${item.count} reports`} 
                  color="secondary" 
                  size="small"
                />
              </Box>
            ))}
          </Paper>
        </Box>

        {/* Charts Section */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
            Detailed Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Interactive charts showing detailed breakdown of car reports data
          </Typography>
          
          <AnalyticsCharts
            dealershipPrices={analyticsData.dealershipPrices}
            dealershipReportCounts={analyticsData.dealershipReportCounts}
            dealershipUserCounts={analyticsData.dealershipUserCounts}
            vehicleMakes={analyticsData.vehicleMakes}
            vehicleModels={analyticsData.vehicleModels}
            stateStats={analyticsData.stateStats}
          />
        </Paper>

        {/* Footer Info */}
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Data excludes reports from specified test users • Last updated: {format(new Date(), 'PPpp')}
          </Typography>
        </Box>
      </Container>
    </AppLayout>
  );
};

export default AnalyticsPage;